/**
 * Golden fidelity check: re-evaluate the UNTOUCHED solver baseline with our
 * own evaluator and compare against what the backend reported. If the two
 * agree, cost deltas and recomputed times can be presented as exact; any
 * drift downgrades them to "estimated" and names the first diverging field so
 * the mismatch is diagnosable rather than mysterious.
 *
 * Anchoring: each route is anchored at its route_start departure (falling
 * back to the first work stop's reported arrival), so the check validates the
 * PROPAGATION semantics (transit floors, service-on-outgoing-arc, waiting,
 * lookup conventions) independently of where the solver chose to start the
 * route.
 */

import { evaluateRoute, groupRouteStops } from './evaluator';
import { clockStrToMinutes } from './timeConversion';

const REL_TOL = 1e-6;
const MAX_DIVERGENCES = 25;

const num = (v) => {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? null : n;
};

const timeOf = (row, intField, strField) => num(row[intField]) ?? clockStrToMinutes(row[strField]);

const floatsDiffer = (a, b) => {
  if (a == null || b == null) return a !== b && !(a == null && b == null);
  const scale = Math.max(1, Math.abs(a), Math.abs(b));
  return Math.abs(a - b) / scale > REL_TOL;
};

export function goldenCheck(baselineData, ctx) {
  const divergences = [];
  let checkedStops = 0;
  let checkedRoutes = 0;

  const report = (vehicleId, stopNumber, field, expected, got) => {
    if (divergences.length < MAX_DIVERGENCES) {
      divergences.push({ vehicleId, stopNumber, field, expected, got });
    }
  };

  const byVehicle = groupRouteStops(baselineData.route_stops || []);
  for (const [vehicleId, rows] of byVehicle) {
    if (rows.length < 2) continue;
    const anchor =
      timeOf(rows[0], 'departureTimeInt', 'departureTimeStr') ??
      timeOf(rows[1], 'arrivalTimeInt', 'arrivalTimeStr');
    if (anchor == null) continue; // integer-less scenario — nothing to validate against
    checkedRoutes += 1;

    const evaluated = evaluateRoute(vehicleId, rows, ctx, { anchorMinutes: anchor });
    for (let i = 0; i < rows.length; i++) {
      const reported = rows[i];
      const computed = evaluated.stops[i];
      checkedStops += 1;

      const reportedArrival = timeOf(reported, 'arrivalTimeInt', 'arrivalTimeStr');
      if (i > 0 && reportedArrival != null && computed.arrivalInt !== reportedArrival) {
        report(vehicleId, reported.stopNumber, 'arrivalTimeInt', reportedArrival, computed.arrivalInt);
      }
    }

    // Reported per-row cost/duration conventions, revalidated via our lookups.
    const vehicle = ctx.vehicleInfo(vehicleId);
    const vehicleTypeId = vehicle ? vehicle.vehicleTypeId : null;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const next = i + 1 < rows.length ? rows[i + 1] : null;
      const prev = i > 0 ? rows[i - 1] : null;
      if (next && String(next.locationId) !== String(row.locationId)) {
        const aware = ctx.cost.typeAware(row.locationId, next.locationId, vehicleTypeId);
        if (floatsDiffer(num(row.travelCostTo), num(aware && aware.cost) ?? 0)) {
          report(vehicleId, row.stopNumber, 'travelCostTo', num(row.travelCostTo), num(aware && aware.cost) ?? 0);
        }
      }
      if (prev && String(prev.locationId) !== String(row.locationId)) {
        const agnostic = ctx.cost.typeAgnostic(prev.locationId, row.locationId);
        if (floatsDiffer(num(row.travelDurationFromPreviousStop), num(agnostic && agnostic.duration) ?? 0)) {
          report(
            vehicleId,
            row.stopNumber,
            'travelDurationFromPreviousStop',
            num(row.travelDurationFromPreviousStop),
            num(agnostic && agnostic.duration) ?? 0
          );
        }
      }
    }
  }

  // Summary totals against the backend's own summary.
  const summary = baselineData.resultSummary;
  if (summary && summary.routeSummary) {
    const stops = baselineData.route_stops || [];
    const sum = (field) => stops.reduce((acc, s) => acc + (num(s[field]) ?? 0), 0);
    if (floatsDiffer(num(summary.routeSummary.totalTravelCost), sum('travelCostTo'))) {
      report(null, null, 'totalTravelCost', num(summary.routeSummary.totalTravelCost), sum('travelCostTo'));
    }
  }

  return {
    clean: divergences.length === 0,
    checkedRoutes,
    checkedStops,
    divergences,
    firstDivergence: divergences[0] ?? null,
  };
}
